import { CardListType, CardType, RecCardType } from '@/types/card'
import { groupByModes } from '@/utils/constants'

type Props = {
  cardLists: CardListType[]
  cardMap: Record<string, CardType>
  groupedBy: (typeof groupByModes)[number]['key']
  initCardLists: CardListType[]
  priceSource: keyof CardType['prices']
}

const group = ({ cardLists, cardMap, groupedBy, initCardLists, priceSource }: Props) => {
  const seen = new Set<string>()
  const flattened = cardLists
    .filter((cardList) => cardLists.length < 2 || !cardList.header.endsWith('Commanders')) // Exclude Commander lists when grouping
    .flatMap((cardList) => cardList.cardviews)
    // Exclude duplicates based on card name
    .filter((card) => {
      if (seen.has(card.name)) return false
      seen.add(card.name)
      return true
    })
  switch (groupedBy) {
    case 'default':
      return initCardLists
    case 'date': {
      const reducer = (
        acc: Record<string, { cardviews: RecCardType[]; header: string; tag: string }>,
        card: RecCardType,
      ) => {
        const dateRanges = [
          /* eslint-disable sort/object-properties */
          { label: 'Last 3 Months', date: new Date(new Date().setDate(new Date().getDate() - 90)) },
          { label: 'Last 6 Months', date: new Date(new Date().setDate(new Date().getDate() - 180)) },
          { label: 'Last Year', date: new Date(new Date().setDate(new Date().getDate() - 365)) },
          { label: 'Last 2 Years', date: new Date(new Date().setDate(new Date().getDate() - 730)) },
          { label: 'Since 2020', date: new Date('2020-01-01') },
          { label: 'Since 2010', date: new Date('2010-01-01') },
          { label: 'Since 2000', date: new Date('2000-01-01') },
          { label: 'Earliest', date: new Date('0000-01-01') },
          /* eslint-enable sort/object-properties */
        ]
        const released_at = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.released_at
        if (!released_at) return acc
        const dateRange =
          dateRanges.find(({ date }) => new Date(released_at) > date) || dateRanges[dateRanges.length - 1]
        const i = dateRanges.indexOf(dateRange)
        const { label } = dateRanges[i]
        acc[i] = {
          cardviews: [...(acc[i]?.cardviews || []), card],
          header: label,
          tag: label,
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'mana': {
      const reducer = (acc: Record<string, RecCardType[]>, card: RecCardType) => {
        const cmc =
          card.cards && cardMap[card.cards[0].url].legal_commander
            ? cardMap[card.cards[0].url].cmc + cardMap[card.cards[1].url].cmc
            : cardMap[card.sanitized_wo].cmc
        if (!acc[cmc]) acc[cmc] = []
        acc[cmc].push(card)
        return acc
      }
      const sorted = Object.entries(flattened.reduce(reducer, {})).map(
        ([cmc, grouped]) =>
          ({
            cardviews: grouped,
            header: `${cmc} Mana`,
            tag: `${cmc}`,
          }) as CardListType,
      )
      return sorted
    }
    case 'price': {
      const priceRanges = [
        { label: '< $0.25', max: 0.25, min: 0 },
        { label: '$0.25 - $0.50', max: 0.5, min: 0.25 },
        { label: '$0.50 - $1', max: 1, min: 0.5 },
        { label: '$1 - $2.50', max: 2.5, min: 1 },
        { label: '$2.50 - $5', max: 5, min: 2.5 },
        { label: '$5 - $10', max: 10, min: 5 },
        { label: '$10 - $20', max: 20, min: 10 },
        { label: '$20 - $50', max: 50, min: 20 },
        { label: '$50 - $100', max: 100, min: 50 },
        { label: '$100+', max: Infinity, min: 100 },
      ]
      const reducer = (
        acc: Record<string, { cardviews: RecCardType[]; header: string; tag: string }>,
        card: RecCardType,
      ) => {
        const price = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.prices[priceSource]?.price
        if (!price) {
          return {
            ...acc,
            [priceRanges.length]: {
              cardviews: [...(acc[priceRanges.length]?.cardviews || []), card],
              header: 'Unknown',
              tag: 'Unknown',
            },
          }
        }
        const priceRange = priceRanges.find(({ max }) => price < max) || priceRanges[priceRanges.length - 1]
        const i = priceRanges.indexOf(priceRange)
        const { label } = priceRanges[i]
        acc[i] = {
          cardviews: [...(acc[i]?.cardviews || []), card],
          header: label,
          tag: label,
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      ) as CardListType[]
      return sorted
    }
    case 'rarity': {
      const rarities = ['mythic', 'rare', 'uncommon', 'common', 'special']
      const reducer = (
        acc: Record<string, { cardviews: RecCardType[]; header: string; tag: string }>,
        card: RecCardType,
      ) => {
        const rarity = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.rarity
        if (!rarity) return acc
        const i = rarities.indexOf(rarity)
        acc[i] = {
          cardviews: [...(acc[i]?.cardviews || []), card],
          header: rarity[0].toUpperCase() + rarity.slice(1),
          tag: rarity,
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'type': {
      const types = ['Creature', 'Instant', 'Sorcery', 'Artifact', 'Enchantment', 'Battle', 'Planeswalker', 'Land']
      const reducer = (
        acc: Record<string, { cardviews: RecCardType[]; header: string; tag: string }>,
        card: RecCardType,
      ) => {
        const type = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.primary_type
        if (!type) return acc
        const i = types.indexOf(type)
        acc[i] = {
          cardviews: [...(acc[i]?.cardviews || []), card],
          header: type,
          tag: type,
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'ungrouped':
      return [{ cardviews: flattened, header: '', tag: '' }]
    default:
      throw new Error(`Unknown group mode: ${groupedBy}`)
  }
}

export default group
